// The last line of defence between a thrown error and a white screen.
//
// Game Lite had no boundary at all: any render throw — a malformed wine row, a
// chunk that went missing mid-deploy, a null someone didn't expect — blanked the
// page permanently, mid-tasting, with no way back and no record that it had
// happened. This catches it and gives the player something to press.
//
// The recovery is deliberately staged:
//
//   "Try again"  re-mounts the subtree. Enough for a transient render error, and
//                it keeps the player exactly where they were.
//   "Reload"     a full reload. The session and every unsent answer live in
//                localStorage, so this costs the player nothing — which is worth
//                saying on screen, because it is the thing they're afraid of.
//
// A chunk error skips straight to reload-shaped copy: re-mounting can't conjure
// a chunk that isn't on the CDN any more.

import { Component } from 'react';
import { isChunkLoadError } from '../../utils/lazyWithRetry';
import { reportLoss } from '../../utils/sentry';

/** Where a caught error goes. reportLoss consoles it AND files it with Sentry. */
function report(error, info, scope) {
  reportLoss('lite.render_crash', error, {
    scope,
    componentStack: info?.componentStack || '',
    chunkError: isChunkLoadError(error),
  });
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    report(error, info, this.props.scope || 'app');
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps) {
    // A route-level boundary resets when the location changes, so one bad screen
    // doesn't poison every screen after it. Without this, navigating away from a
    // crashed page keeps showing the crash.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleRetry = () => this.setState({ error: null });

  handleReload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isChunkLoadError(error);

    return (
      <div
        role="alert"
        className="flex min-h-[100dvh] flex-col items-center justify-center bg-wine-background px-6 text-center"
      >
        <div className="w-full max-w-sm">
          <p className="font-display text-xl font-bold text-wine-text-heading">
            {stale ? 'The game just updated' : 'Something went wrong'}
          </p>
          <p className="mt-2 font-body text-sm text-wine-text-body/80">
            {stale
              ? 'Reload to pick up the new version and carry on where you left off.'
              : "That screen hit a snag. Your answers are saved on this phone — nothing is lost."}
          </p>

          <div className="mt-6 space-y-3">
            {!stale && (
              <button
                type="button"
                onClick={this.handleRetry}
                className="w-full rounded-lg bg-wine-primary px-5 py-3 font-medium text-white transition-colors hover:bg-wine-primary/90"
              >
                Try again
              </button>
            )}
            <button
              type="button"
              onClick={this.handleReload}
              className={
                stale
                  ? 'w-full rounded-lg bg-wine-primary px-5 py-3 font-medium text-white transition-colors hover:bg-wine-primary/90'
                  : 'w-full rounded-lg border border-wine-border px-5 py-3 font-medium text-wine-text-body transition-colors hover:bg-wine-card'
              }
            >
              Reload the game
            </button>
          </div>
        </div>
      </div>
    );
  }
}
